import type { SimCastMember } from '@cruz/shared';
import { useState } from 'react';
import { relationLabel, upperFirst } from './format.ts';
import { Sheet } from './Sheet.tsx';
import { Link, Meter } from './ui.tsx';

/**
 * Who is who in an event: a row of small cards (portrait, name and role) above the text.
 * Tapping one shows the description and how you get on. The portrait is a comic face from
 * the library (portraits.ts in the API), the same all the career, so faces become familiar;
 * a monogram in the colour of the role if there is none.
 */

/** One colour per role, dark enough for ivory initials (contrast ≥ 4.5). */
const ROLE_COLORS: Record<string, string> = {
  chief: '#482052',
  tramo_deputy: '#26365e',
  capataz: '#5a3a24',
  priosta: '#6a303a',
  friend: '#1f4e5a',
  rival: '#3b4252',
  grandmother: '#5b2a68',
  marchas: '#4a5a2a',
  neighbor: '#765a2a',
};
const SECONDARY_COLORS = ['#3a1943', '#2f4858', '#5c3d2e', '#36454f', '#4b3b5c'];

function colorOf(id: string): string {
  const fixed = ROLE_COLORS[id];
  if (fixed) return fixed;
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return SECONDARY_COLORS[h % SECONDARY_COLORS.length]!;
}

const initials = (name: string): string =>
  name
    .split(',')[0]!
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export function Portrait({
  id,
  name,
  src = null,
  size = 40,
}: {
  id: string;
  name: string;
  /** The comic portrait; without one, a monogram */
  src?: string | null;
  size?: number;
}) {
  if (src) {
    // Small, the face is what counts: zoom in on the head (the pictures show the shoulders)
    const zoom = size <= 48 ? 1.6 : size <= 64 ? 1.3 : 1;
    return (
      <span
        aria-hidden
        className="flex-none overflow-hidden rounded-full ring-2 ring-gold/70"
        style={{ width: size, height: size, background: colorOf(id) }}
      >
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          style={{ transform: `scale(${zoom})`, transformOrigin: '50% 22%' }}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="flex flex-none items-center justify-center rounded-full font-serif font-semibold text-cream ring-2 ring-gold/70"
      style={{ width: size, height: size, background: colorOf(id), fontSize: size * 0.4 }}
    >
      {initials(name)}
    </span>
  );
}

/** "Rafael Ortega, el Chico" → "Rafael" for the chip; the sheet shows the full name. */
const shortName = (name: string) => name.split(/[\s,]/)[0]!;

export function CastRow({ cast }: { cast: SimCastMember[] }) {
  const [open, setOpen] = useState<SimCastMember | null>(null);
  if (!cast.length) return null;
  return (
    <>
      <ul aria-label="Quién sale" className="mt-3 flex flex-wrap gap-2">
        {cast.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => setOpen(c)}
              aria-label={`${c.name}, ${c.role}`}
              className="flex min-h-11 items-center gap-2 rounded-full border border-purple/15 bg-cream py-1 pr-3 pl-1 text-left transition-colors hover:border-gold dark:border-cream/15 dark:bg-night-700"
            >
              <Portrait id={c.id} name={c.name} src={c.portrait} size={40} />
              <span className="leading-tight">
                <span className="block text-[0.95rem] font-semibold">{shortName(c.name)}</span>
                <span className="block max-w-[11rem] truncate text-[0.75rem] text-ink-soft dark:text-cream/70">
                  {c.role}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <Sheet title={open.name} onClose={() => setOpen(null)}>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Portrait id={open.id} name={open.name} src={open.portrait} size={96} />
              <p className="text-lg">{upperFirst(open.description)}</p>
            </div>
            <div>
              <p className="mb-1.5 flex justify-between text-sm">
                <span className="text-ink-soft dark:text-cream/75">Vuestra relación</span>
                <span className="font-semibold text-gold-700 dark:text-gold-300">
                  {relationLabel(open.relation)}
                </span>
              </p>
              <Meter value={(open.relation + 10) * 5} label={`Relación con ${open.name}`} />
            </div>
            <Link to="/simulador/personajes">Ver a toda la gente de tu hermandad</Link>
          </div>
        </Sheet>
      )}
    </>
  );
}
